import { useQuery } from '@apollo/client/react'
import { dedupe } from '@/helpers/dedupe'
import { Table as BaseTable, type TableProps } from '../table'
import { usePageInfo } from '@/hooks/usePageInfo'
import { Slimline as Error } from '@/components/error'
import { LoadingSkeleton } from '@/components/base/loading-skeleton'
import { Badge } from '../base/badge'
import { graphql } from '@/gql'
import type { IntegrationFilters, IntegrationOrder } from '@/gql/graphql'

const IntegrationsQuery = graphql(`
  query Integrations($filters: IntegrationFilters, $order: IntegrationOrder, $after: String) {
    integrations(filters: $filters, order: $order, after: $after) {
      nodes {
        id
        integrationProvider { id displayName }
        syncState
      }
      count
      pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
    }
  }
`)

interface Props extends TableProps {
  filters?: IntegrationFilters
}

export const Table = ({ filters = {}, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery(IntegrationsQuery, { variables: { filters } })

  const { pageInfo } = usePageInfo(data?.integrations?.pageInfo)

  if (loading) return <LoadingSkeleton table />

  return (
    <>
      { error && <Error error={ error } /> }
      { data && <BaseTable<IntegrationFilters, IntegrationOrder>
        { ...props }
        initialFilters={ filters }
        moreToFetch={ pageInfo.current?.hasNextPage }
        refetch={ refetch }
        totalRows={ data.integrations.count }
        fetchMore={ () => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.integrations.nodes = dedupe([...previousData.integrations.nodes, ...fetchMoreResult.integrations.nodes])
            return { ...fetchMoreResult }
          }
        }) }
        headings={ [
          { name: 'Type' },
          { name: 'Status' },
        ] }
        rows={ data?.integrations?.nodes.map(integration => ({
          id: integration.id,
          columns: [
            integration.integrationProvider.displayName,
            integration.syncState ? <Badge color="green">Syncing</Badge> : <Badge color="red">Not syncing</Badge>
          ],
          link: `/integrations/${integration.id}`,
        })) }
      /> }
    </>
  )
}
